/**
 * Jul-2026 (User-Wunsch) — KI-Wissensdatenbank: Händler laden eigene Unterlagen
 * (PDF/TXT/MD/CSV) hoch; Bikevinci beantwortet Fragen daraus mit Quellenangabe.
 */
import { useEffect, useRef, useState } from "react";
import { BookOpenText, Upload, Trash2, Loader2, FileText } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

export default function AssistantKnowledgeSection() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/assistant/knowledge");
            setItems(data.items || []);
        } catch { /* noop */ }
    };
    useEffect(() => { load(); }, []);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/assistant/knowledge/upload", fd);
            toast.success(`„${data.document?.filename}“ hochgeladen — die KI kennt den Inhalt jetzt.`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const remove = async (id) => {
        setDeletingId(id);
        try {
            await api.delete(`/assistant/knowledge/${id}`);
            setItems((l) => l.filter((x) => x.id !== id));
            toast.success("Dokument entfernt.");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="assistant-knowledge-section">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                aria-expanded={open}
                data-testid="assistant-knowledge-toggle"
            >
                <BookOpenText className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-bold">KI-Wissensdatenbank {items.length > 0 && <span className="ml-1 text-xs font-black bg-[#FFCC00] text-[#111827] rounded-sm px-1.5 py-0.5 align-middle">{items.length}</span>}</h2>
                    <p className="text-xs text-gray-600 mt-1">Eigene Unterlagen (PDF, TXT, MD, CSV) hochladen — Bikevinci beantwortet Fragen daraus mit Quellenangabe. Gilt org-weit für alle Standorte.</p>
                </div>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${open ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>
            {open && (
                <div className="border-t border-gray-200 p-6 space-y-4">
                    <div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".pdf,.txt,.md,.csv"
                            className="hidden"
                            onChange={(e) => upload(e.target.files?.[0])}
                            data-testid="assistant-knowledge-file-input"
                        />
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            className="inline-flex items-center gap-2 h-9 px-4 rounded-sm bg-[#111827] hover:bg-black text-white text-sm font-bold disabled:opacity-50 transition-colors"
                            data-testid="assistant-knowledge-upload-btn"
                        >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            {uploading ? "Wird verarbeitet …" : "Dokument hochladen"}
                        </button>
                        <p className="text-[11px] text-gray-500 mt-1.5">Max. 15 MB. Z. B. Hersteller-Garantiebedingungen, Preislisten, interne Arbeitsanweisungen. Scan-PDFs ohne Textebene werden nicht unterstützt.</p>
                    </div>
                    {items.length === 0 ? (
                        <p className="text-sm text-gray-500" data-testid="assistant-knowledge-empty">Noch keine Dokumente hochgeladen.</p>
                    ) : (
                        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-sm" data-testid="assistant-knowledge-list">
                            {items.map((it) => (
                                <li key={it.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`assistant-knowledge-item-${it.id}`}>
                                    <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold truncate">{it.filename}</div>
                                        <div className="text-[11px] text-gray-500">
                                            {it.pages ? `${it.pages} Seiten · ` : ""}{it.chunk_count} Abschnitte
                                            {it.uploaded_by_name ? ` · von ${it.uploaded_by_name}` : ""}
                                            {it.created_at ? ` · ${new Date(it.created_at).toLocaleDateString("de-DE")}` : ""}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => remove(it.id)}
                                        disabled={deletingId === it.id}
                                        className="h-8 w-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-sm shrink-0"
                                        title="Löschen"
                                        data-testid={`assistant-knowledge-delete-${it.id}`}
                                    >
                                        {deletingId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
